import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { PaymentPlanDto } from 'src/api/paymentPlanService/dto/PaymentPlanDto';
import { isUserAuthenticated, logoutUser } from 'src/auth/auth0';
import { Modal } from 'src/components/modal/modal';
import { queryClient } from 'src/lib/query-client';
import { setBasket, updateBasketTracking } from 'src/store/slices/basket-slice';
import { setBookingDetails, setBuildingData, setRoomData } from 'src/store/slices/booking-slice';
import { setCreatedPaymentPlan, setUserChoices } from 'src/store/slices/payment-plan-slice';
import { selectTokenExpired, setDetails, setUserHasBookings } from 'src/store/slices/user-slice';
import store from 'src/store/store';
import { globalStyles } from 'src/styles/global-styles';
import { getTheme } from 'src/utils/getTheme';
import { useHandleUserAuth } from 'src/utils/handle-user-auth';
import {
    getBasketStorage,
    getBookingStorage,
    getPaymentPlanSelectionStorage,
    getUserStorage,
} from 'src/utils/local-storage-versioning';
import { useGetUserHasBookings } from 'src/lib/hooks/use-get-user-has-bookings';

interface AppProviderProps {
    children: JSX.Element;
}

const AppInitializer: FC<{ setModalOpen: (open: boolean) => void }> = ({ setModalOpen }) => {
    const isTokenExpired = useSelector(selectTokenExpired);
    const dispatch = useDispatch();
    const { handleUserAuth } = useHandleUserAuth();
    const [userSession, setUserSession] = useState<boolean>(false);
    const { data: userHasBookings } = useGetUserHasBookings(userSession);

    const getCookie = (name: string): string | null => {
        const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
        return match ? match[2] : null;
    };

    useEffect(() => {
        const initializeApp = async () => {
            const session = await isUserAuthenticated();

            if (session) {
                setUserSession(true);
                handleUserAuth(session);
            }

            const storedBasket = getBasketStorage();
            const bookingStorage = getBookingStorage();
            const paymentPlanSelectionStorage = getPaymentPlanSelectionStorage();
            const user = getUserStorage();

            if (storedBasket) {
                dispatch(setBasket(storedBasket));
            }

            if (bookingStorage) {
                const { room, details, building } = bookingStorage;

                if (room) {
                    dispatch(setRoomData(room));
                }
                if (details) {
                    dispatch(setBookingDetails(details));
                }
                if (building) {
                    dispatch(setBuildingData(building));
                }
            }

            if (paymentPlanSelectionStorage) {
                const { createdPaymentPlan, userChoices } = paymentPlanSelectionStorage;
                dispatch(setCreatedPaymentPlan(new PaymentPlanDto(createdPaymentPlan)));
                dispatch(setUserChoices(userChoices));
            }

            if (user) {
                dispatch(setDetails(user));
            }

            const vitaGclid = getCookie('_vita_gclid');
            const vitaUtmMedium = getCookie('_vita_utm_medium');
            const vitaUtmSource = getCookie('_vita_utm_source');
            const tracking = {
                lead_source: vitaGclid,
                lead_medium: vitaUtmMedium,
                lead_campaign: vitaUtmSource,
            };
            dispatch(updateBasketTracking(tracking));
        };
        initializeApp();
    }, []);

    useEffect(() => {
        if (userHasBookings) {
            dispatch(setUserHasBookings(userHasBookings));
        }
    }, [userHasBookings]);

    useEffect(() => {
        if (isTokenExpired) {
            setModalOpen(true);
        } else {
            setModalOpen(false);
        }
    }, [isTokenExpired, setModalOpen]);

    return null;
};

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const { theme } = getTheme();

    const handleClose = () => {
        logoutUser();
        setModalOpen(false);
    };

    return (
        <QueryClientProvider client={queryClient}>
            <Provider store={store}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <GlobalStyles styles={globalStyles} />
                    <BrowserRouter>
                        <AppInitializer setModalOpen={setModalOpen} />
                        {children}
                        <Modal open={modalOpen} handleClose={handleClose} />
                    </BrowserRouter>
                </ThemeProvider>
            </Provider>
        </QueryClientProvider>
    );
};
